"use client";

import React, { useState } from "react";
import { Flame, Zap, Gauge, ShieldCheck, Wrench, TriangleAlert as AlertTriangle, CircleQuestionMark as HelpCircle, PhoneCall, CircleCheck as CheckCircle2, Clock, Award, ChevronRight, FileText } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { motion, useReducedMotion } from "framer-motion";
const productLines = [{
  id: "gas",
  name: "Atmospheric & Power Direct Vent Gas",
  tagline: "Defender Safety System with Hydrojet Total Performance System",
  description: "Industry-standard residential and commercial gas water heaters known for rugged reliability, self-cleaning cold water inlet pipes, and low NOx combustion.",
  features: ["Hydrojet Total Performance System minimizes sediment buildup", "ICON System intelligent gas control valve with status LED", "Defender Safety System with Resettable Thermal Switch", "Vitraglas enamel lining for premium corrosion resistance"],
  commonModels: "RG250T6N, RG240T6N, PDX250T6FBN",
  repairRoute: "/gas-water-heater-service/"
}, {
  id: "electric",
  name: "AeroTherm Series Hybrid Heat Pump",
  tagline: "High-efficiency ENERGY STAR qualified electric solutions",
  description: "Combines ambient air heat capture with standard electric elements, lowering domestic electrical water heating bills by up to 70% while providing continuous hot water.",
  features: ["Multiple operation modes: Hybrid, Efficiency, Electric, Vacation", "Quiet operation with built-in air filter monitoring", "Vitraglas lining with dual magnesium anode protection", "First-hour hot water delivery up to 87 gallons"],
  commonModels: "RE2H50R10, RE2H65R10, RE2H80R10",
  repairRoute: "/electric-water-heater-service/"
}, {
  id: "tankless",
  name: "Infiniti Series Tankless Water Heaters",
  tagline: "On-demand high-efficiency condensing gas technology",
  description: "Wall-hung units designed with durable dual stainless steel heat exchangers that deliver uninterrupted hot water with top-tier thermal efficiency.",
  features: ["Scale Reduction Technology (SRT) prevents mineral scaling", "Up to 0.95 UEF high-efficiency condensing operation", "Built-in recirculation pump ready models available", "Compact footprint suited for basement, closet, or utility room"],
  commonModels: "K-Series, L-Series (RTG-199N, RTG-160N)",
  repairRoute: "/tankless-water-heater-service/"
}];
const commonIssues = [{
  title: "ICON Gas Valve Flashing Codes",
  cause: "Thermopile millivolt voltage drop or pilot flame sensor obstruction.",
  solution: "Clean pilot orifice, check thermopile output (>350mV), or replace the ICON smart gas thermostat.",
  severity: "Immediate Service"
}, {
  title: "Sediment Build-up In Tank",
  cause: "Failure of the Hydrojet dip tube or mineral-dense municipal water supply.",
  solution: "Full chemical tank flush, replacement of degraded Hydrojet dip tube, and magnesium anode inspection.",
  severity: "Routine Maintenance"
}, {
  title: "Thermal Switch Tripping",
  cause: "Insufficient combustion air supply or lint-clogged flame arrester screen.",
  solution: "Clear base air intake, inspect burner chamber airflow, reset safety switch according to manual.",
  severity: "Safety Warning"
}, {
  title: "Heat Pump Error Codes (E01-E04)",
  cause: "Restricted air filter, ambient temperature below operating limits, or fan sensor fault.",
  solution: "Clean air filter screen, verify airflow clearances, and inspect evaporator coil sensors.",
  severity: "Urgent Repair"
}];
const troubleshootingSteps = [{
  step: "1",
  title: "Check Status Light",
  detail: "Locate the LED on the Honeywell/ICON gas control valve or digital interface. Count the blink flashes (e.g., 4 blinks = high temperature limit)."
}, {
  step: "2",
  title: "Inspect Pilot & Gas Supply",
  detail: "Verify the gas shut-off valve is fully open. For standard gas models, check the glass sight port to see if the pilot remains ignited."
}, {
  step: "3",
  title: "Verify Circuit Breakers",
  detail: "For AeroTherm hybrid or standard electric models, confirm the double-pole 30A breaker in your main electrical panel is untripped."
}, {
  step: "4",
  title: "Locate Serial & Model Number",
  detail: "Find the data rating plate on the side of the tank to verify manufacturer warranty dates before requesting official parts dispatch."
}];
const faqList = [{
  q: "How do I check if my Bradford White water heater is still under warranty?",
  a: "Bradford White units carry standard 6-year or 10-year limited tank and parts warranties. You can check warranty status using the 10-character serial number on your unit's data plate (the first two letters determine year and month of manufacture). Our licensed plumbers can look this up directly during your service visit."
}, {
  q: "Why is the status light on my Bradford White ICON valve blinking?",
  a: "A normal status light blinks once every 3 seconds ('Heartbeat'). Repeated multiple blinks indicate diagnostic error codes such as low thermopile voltage (2 blinks), high limit temperature fault (4 blinks), or sensor failure (5 blinks). Contact our technicians to interpret and resolve fault codes safely."
}, {
  q: "Do you supply genuine Bradford White replacement parts in Jonesboro?",
  a: "Yes. We stock authentic Bradford White OEM components including ICON gas valves, Hydrojet dip tubes, Resettable Thermal Switches, electric elements, and pilot assembly kits for immediate repairs."
}, {
  q: "How often should I flush my Bradford White water heater?",
  a: "Even though Bradford White's Hydrojet Total Performance System reduces sediment naturally, we recommend a professional tank flush and anode rod inspection once every 12 months to maintain maximum energy efficiency and extend tank life."
}];
export default function BradfordWhiteWaterHeaterServices() {
  const [selectedTab, setSelectedTab] = useState("gas");
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08,
        duration: 0.3
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 15
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4
      }
    }
  };
  return <section id="bradfordwhitewaterheaterservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 font-medium">
              <Award className="w-3.5 h-3.5 text-accent-foreground mr-1" />
              Certified Brand Specialists
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Bradford White Water Heater Repair & Installation
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Authorized troubleshooting, OEM part replacement, and emergency service for all residential and commercial Bradford White water heating systems in Jonesboro and surrounding areas.
          </p>
        </div>

        {/* Emergency Alert Banner */}
        <div className="mb-12">
          <Alert className="border-border bg-card shadow-xs">
            <AlertTriangle className="h-5 w-5 text-accent" />
            <AlertTitle className="text-card-foreground font-semibold">
              Emergency Bradford White Breakdown?
            </AlertTitle>
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-1">
              <span>
                Our master plumbers are on call 24 hours a day with stocked trucks carrying Experienced parts.
              </span>
              <div className="flex items-center gap-3">
                <Button size="sm" asChild>
                  <Link to="/emergency-plumbing/">
                    <PhoneCall className="w-4 h-4 mr-1" /> 24/7 Emergency Dispatch
                  </Link>
                </Button>
                <Button size="sm" variant="outline" asChild>
                  <Link to="/contact/">Request Quote</Link>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        </div>

        {/* Product Lines Tabs */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <h3 className="text-2xl font-bold font-default text-foreground">
                Product Lines & Models We Service
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Select your unit type to explore diagnostic capabilities and model specifications.
              </p>
            </div>
          </div>

          <Tabs value={selectedTab} onValueChange={setSelectedTab} className="w-full">
            <TabsList className="grid grid-cols-3 w-full max-w-md mx-auto md:mx-0 mb-6">
              <TabsTrigger value="gas" className="flex items-center gap-2">
                <Flame className="w-4 h-4 text-accent" />
                <span>Gas Systems</span>
              </TabsTrigger>
              <TabsTrigger value="electric" className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-accent" />
                <span>Hybrid & Electric</span>
              </TabsTrigger>
              <TabsTrigger value="tankless" className="flex items-center gap-2">
                <Gauge className="w-4 h-4 text-accent" />
                <span>Tankless Series</span>
              </TabsTrigger>
            </TabsList>

            {productLines.map((line, index) => <TabsContent key={line.id} value={line.id} data-index={index}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch bg-card p-6 sm:p-8 rounded-xl border border-border">
                  <div className="lg:col-span-7 flex flex-col justify-between">
                    <div>
                      <Badge variant="outline" className="mb-3 border-border bg-background">
                        Series Overview
                      </Badge>
                      <h4 className="text-2xl font-semibold font-default text-card-foreground mb-2">
                        {line.name}
                      </h4>
                      <p className="text-sm font-medium text-muted-foreground mb-4">
                        {line.tagline}
                      </p>
                      <p className="text-sm text-card-foreground/90 leading-relaxed mb-6">
                        {line.description}
                      </p>

                      <div className="space-y-2.5 mb-6">
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                          Engineered Advantages
                        </span>
                        {line.features.map((feat, featIdx) => <div key={featIdx} data-index={featIdx} className="flex items-start gap-2.5">
                            <CheckCircle2 className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                            <span className="text-sm text-card-foreground">{feat}</span>
                          </div>)}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <span className="text-xs text-muted-foreground block">Common Model Series</span>
                        <span className="text-sm font-mono font-medium text-card-foreground">{line.commonModels}</span>
                      </div>
                      <Button asChild>
                        <Link to={line.repairRoute}>
                          View Specialized Repair <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="lg:col-span-5 bg-background p-6 rounded-lg border border-border flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <ShieldCheck className="w-5 h-5 text-accent" />
                        <h5 className="font-semibold text-base text-foreground font-default">
                          Service Standards
                        </h5>
                      </div>
                      <ul className="space-y-3 text-sm text-muted-foreground mb-6">
                        <li className="flex items-start gap-2">
                          <Clock className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                          <span>Same-day diagnostic appointments for no-hot-water emergencies.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Wrench className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                          <span>Strict adherence to Bradford White factory torque and pressure specs.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <FileText className="w-4 h-4 text-accent shrink-0 mt-0.5" />
                          <span>Direct claim filing support for active warranty tanks and components.</span>
                        </li>
                      </ul>
                    </div>
                    <div className="bg-muted p-4 rounded-md">
                      <p className="text-xs text-muted-foreground leading-normal">
                        <strong>Pro Tip:</strong> Never reset a Bradford White thermal switch more than once without a professional chamber airflow inspection.
                      </p>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>

        {/* Common Issues Grid */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h3 className="text-2xl font-bold font-default text-foreground mb-2">
              Common Bradford White Issues We Fix
            </h3>
            <p className="text-sm text-muted-foreground">
              Direct answers to frequent symptoms experienced by residential and light commercial units.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commonIssues.map((issue, idx) => <div key={idx} data-index={idx}>
                <Card className="h-full flex flex-col justify-between border-border hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <Badge variant="outline" className="w-fit mb-2 text-xs border-border">
                      {issue.severity}
                    </Badge>
                    <CardTitle className="text-base font-semibold font-default">
                      {issue.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 text-xs flex-1">
                    <div>
                      <span className="font-medium text-foreground block mb-0.5">Likely Cause:</span>
                      <p className="text-muted-foreground">{issue.cause}</p>
                    </div>
                    <div>
                      <span className="font-medium text-foreground block mb-0.5">Our Solution:</span>
                      <p className="text-muted-foreground">{issue.solution}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>)}
          </div>
        </div>

        {/* Troubleshooting & Warranty Guide */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-start">
          <div className="lg:col-span-6 bg-card p-6 sm:p-8 rounded-xl border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-lg bg-primary text-primary-foreground">
                <Wrench className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold font-default text-card-foreground">
                  Homeowner Troubleshooting Checklist
                </h3>
                <p className="text-xs text-muted-foreground">
                  Perform these quick preliminary checks before calling for dispatch.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {troubleshootingSteps.map((step, sIdx) => <div key={sIdx} data-index={sIdx} className="flex gap-4 p-3 bg-background rounded-lg border border-border">
                  <span className="flex items-center justify-center size-7 rounded-full bg-primary text-primary-foreground text-xs font-bold shrink-0">
                    {step.step}
                  </span>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">{step.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.detail}</p>
                  </div>
                </div>)}
            </div>

            <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Still experiencing errors?</span>
              <Button size="sm" variant="secondary" asChild>
                <Link to="/water-heater-problems/">Water Heater Diagnostics</Link>
              </Button>
            </div>
          </div>

          <div className="lg:col-span-6 bg-card p-6 sm:p-8 rounded-xl border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-lg bg-primary text-primary-foreground">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-bold font-default text-card-foreground">
                  Warranty & Replacement Support
                </h3>
                <p className="text-xs text-muted-foreground">
                  Protecting your investment with factory-backed coverage.
                </p>
              </div>
            </div>

            <div className="space-y-4 text-sm text-card-foreground/90 leading-relaxed mb-6">
              <p>
                Bradford White water heaters are sold exclusively to licensed plumbing contractors, ensuring proper installation and rigorous safety compliance.
              </p>
              <div className="p-4 bg-muted rounded-lg border border-border">
                <h4 className="font-semibold text-foreground mb-2 text-xs uppercase tracking-wider">
                  Our Warranty Concierge Includes:
                </h4>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                    <span>Instant serial number verification for active tank & part coverage.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                    <span>Hassle-free OEM warranty exchange and core processing.</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-accent" />
                    <span>Complete upgrade options if tank replacement is deemed necessary.</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button className="flex-1" asChild>
                <Link to="/water-heater-replacement/">Explore Replacement Options</Link>
              </Button>
              <Button variant="outline" className="flex-1" asChild>
                <Link to="/water-heater-repair/">General Repair Services</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* FAQs Accordion */}
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold font-default text-foreground mb-2">
              Frequently Asked Questions
            </h3>
            <p className="text-sm text-muted-foreground">
              Everything you need to know regarding Bradford White servicing and warranty claims.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full space-y-3">
            {faqList.map((faq, fIdx) => <AccordionItem key={fIdx} value={`faq-${fIdx}`} data-index={fIdx} className="bg-card border border-border rounded-lg px-4">
                <AccordionTrigger className="text-base font-semibold text-foreground hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pt-0 pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {/* Bottom CTA Card */}
        <div className="mt-16 bg-primary text-primary-foreground rounded-2xl p-8 md:p-12 text-center shadow-lg">
          <h3 className="text-2xl sm:text-3xl font-bold font-default mb-3">
            Need Expert Bradford White Service in Jonesboro?
          </h3>
          <p className="max-w-2xl mx-auto text-primary-foreground/80 text-sm sm:text-base mb-8">
            Speak directly with our licensed water heater specialists. We arrive promptly with factory-approved diagnostic gear and parts.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Button size="lg" variant="secondary" asChild>
              <Link to="/contact/">Schedule Service Call</Link>
            </Button>
            <Button size="lg" variant="outline" className="bg-primary/20 text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground hover:text-primary" asChild>
              <Link to="/water-heater/">All Water Heater Services</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}